import { Button, Empty, Tag, Tooltip } from 'antd';
import multiavatar from '@multiavatar/multiavatar/esm';
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Flame,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { PageActions } from './page-actions';

const avatarSources = new Map<string, string>();
const maximumCachedAvatars = 512;

function avatarSource(seed: string) {
  const cached = avatarSources.get(seed);
  if (cached) return cached;

  const source = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(multiavatar(seed))}`;
  if (avatarSources.size >= maximumCachedAvatars) {
    const oldestSeed = avatarSources.keys().next().value;
    if (oldestSeed) avatarSources.delete(oldestSeed);
  }
  avatarSources.set(seed, source);
  return source;
}

export function IdentityAvatar({
  seed,
  label = '账号',
  size = 36,
  shape = 'circle',
  className = '',
}: {
  seed?: string | null;
  label?: string;
  size?: number;
  shape?: 'circle' | 'rounded';
  className?: string;
}) {
  const normalizedSeed = seed?.trim() || `spark:anonymous:${label}`;
  const source = useMemo(() => avatarSource(normalizedSeed), [normalizedSeed]);

  return (
    <span
      className={`spark-identity-avatar spark-identity-avatar--${shape} ${className}`.trim()}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${label}头像`}
    >
      <img src={source} alt="" aria-hidden="true" />
    </span>
  );
}

export function IdentityLabel({
  seed,
  title,
  meta,
  size = 34,
}: {
  seed: string;
  title: ReactNode;
  meta?: ReactNode;
  size?: number;
}) {
  return (
    <span className="spark-identity-label">
      <IdentityAvatar seed={seed} label={typeof title === 'string' ? title : '账号'} size={size} />
      <span className="spark-identity-label__text">
        <strong>{title}</strong>
        {meta !== undefined && <small>{meta}</small>}
      </span>
    </span>
  );
}

export function SparkMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="spark-brand">
      <span className="spark-brand__mark">
        <img src="/icon.png" alt={compact ? '麦淼 AI' : ''} />
      </span>
      {!compact && (
        <span className="spark-brand__word">
          <span className="spark-brand__cn" aria-label="麦淼">
            <span className="spark-brand__star" aria-hidden="true">麦</span>
            <span className="spark-brand__fire" aria-hidden="true">淼</span>
          </span>
          <em>SPARK</em>
        </span>
      )}
    </span>
  );
}

export function PageHeader({
  title: _title,
  description: _description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return actions ? <PageActions>{actions}</PageActions> : null;
}

export function SectionCard({
  title,
  extra,
  children,
  className = '',
}: {
  title?: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={'spark-section-card ' + className}>
      {(title || extra) && (
        <div className="spark-section-card__header">
          {title ? <h2>{title}</h2> : <span />}
          {extra}
        </div>
      )}
      {children}
    </section>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'blue',
}: {
  label: string;
  value: string;
  hint: string;
  icon: LucideIcon;
  tone?: 'blue' | 'orange' | 'green' | 'purple';
}) {
  return (
    <article className="spark-metric">
      <div className="spark-metric__top">
        <span>{label}</span>
        <span className={'spark-icon-box spark-icon-box--' + tone}>
          <Icon aria-hidden size={17} />
        </span>
      </div>
      <strong>{value}</strong>
      <small>{hint}</small>
    </article>
  );
}

const statusMap = {
  success: { icon: CheckCircle2, className: 'success' },
  running: { icon: Sparkles, className: 'running' },
  queued: { icon: Clock3, className: 'queued' },
  failed: { icon: Flame, className: 'failed' },
} as const;

export function StatusPill({
  status,
  children,
}: {
  status: keyof typeof statusMap;
  children: ReactNode;
}) {
  const config = statusMap[status];
  const Icon = config.icon;
  return (
    <span className={'spark-status spark-status--' + config.className}>
      <Icon aria-hidden size={13} />
      {children}
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="spark-empty">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Points({
  value,
  detail,
}: {
  value: string;
  detail?: string;
}) {
  const content = (
    <span className="spark-points" aria-label={`${value} 积分`}>
      <Flame aria-hidden size={16} />
      <span>{value}</span>
      <span>积分</span>
    </span>
  );
  return detail ? <Tooltip title={`${value} 积分 · ${detail}`}>{content}</Tooltip> : content;
}

export function FeatureLink({
  title,
  description,
  icon: Icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  onClick?: () => void;
}) {
  return (
    <button className="spark-feature-link" type="button" onClick={onClick}>
      <span className="spark-feature-link__icon">
        <Icon aria-hidden size={22} />
      </span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <ArrowRight aria-hidden size={18} />
    </button>
  );
}

export function DemoTag() {
  return (
    <Tag color="blue" variant="filled">
      Mock 数据
    </Tag>
  );
}

export const PrimaryAction = Button;
